import React from "react";
import { useTranslation } from "react-i18next";
import UserProfileHeader from "components/Common/ui/UserProfileHeader";
import Datatable from "../../../components/Common/dataTable"; // our improved table
import AuditLogActionBadge from "components/Common/Badge/AuditLogActionBadge";
import useAuditLogs from "../hooks/useAuditLogs";
import RecordCell from "../components/RecordCell";
import ChangesButton from "../components/ChangesButton";
import { formatToLocaleDate } from "utils/dateUtils";

type AuditLogsTableProps = {
  dataSource: any[];
  loading?: boolean;
  pagination?: boolean;
  dataPaginate?: {
    current: number;
    pageSize: number;
    total: number;
  };
  onChange?: (pagination: any) => void;
};

const safeStr = (v: any) => (v ?? "").toString().toLowerCase();
const safeDate = (v: any) => {
  const t = new Date(v as string).getTime();
  return Number.isFinite(t) ? t : 0;
};

const AuditLogsTable: React.FC<AuditLogsTableProps> = ({
  dataSource,
  loading,
  pagination = false,
  dataPaginate,
  onChange,
}) => {
  const { t } = useTranslation();
  const { mapEntityToKey } = useAuditLogs();

  const columns = [
    {
      title: t("user"),
      key: "user",
      render: (_: any, record: any) => (
        <UserProfileHeader
          photo={record.user?.photo}
          firstName={record.user?.firstName}
          lastName={record.user?.lastName}
          subtitle={record.user?.email}
        />
      ),
      sorter: (a: any, b: any) => {
        const aName = `${safeStr(a.user?.lastName)} ${safeStr(a.user?.firstName)} ${safeStr(a.user?.email)}`;
        const bName = `${safeStr(b.user?.lastName)} ${safeStr(b.user?.firstName)} ${safeStr(b.user?.email)}`;
        return aName.localeCompare(bName);
      },
    },
    {
      title: t("date"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (val: string) => formatToLocaleDate(val, "L LT", "-"),
      sorter: (a: any, b: any) => safeDate(a.createdAt) - safeDate(b.createdAt),
    },
    {
      title: t("action"),
      dataIndex: "action",
      key: "action",
      render: (_: any, record: any) => <AuditLogActionBadge action={record.action} />,
      sorter: (a: any, b: any) => safeStr(a.action).localeCompare(safeStr(b.action)),
    },
    {
      title: t("entity"),
      dataIndex: "entity",
      key: "entity",
      render: (_: any, record: any) => t(mapEntityToKey(record.entity)),
      sorter: (a: any, b: any) =>
        t(mapEntityToKey(a.entity)).localeCompare(t(mapEntityToKey(b.entity))),
    },
    // {
    //   title: "Record",
    //   dataIndex: "recordName",
    //   key: "recordName",
    //   render: (_: any, record: any) => (
    //     <RecordCell entity={record.entity} record={record.record} entityId={record.entityId} />
    //   ),
    // },
    {
      title: t("changes"),
      key: "changes",
      render: (_: any, log: any) => (
        <ChangesButton
          action={log.action}
          changes={log.changes}
          entity={log.entity}
          record={log.record}     // <— important for delete
          entityId={log.entityId}
        />
      ),
    },
  ];

  return (
    <Datatable
      columns={columns}
      dataSource={dataSource}
      loading={loading}
      pagination={pagination}
      dataPaginate={dataPaginate}
      onChange={onChange}
    />
  );
};

export default AuditLogsTable;
